import { useMemo, useState } from 'react';
import { Receipt, Filter } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { SearchInput } from '@/components/ui/SearchInput';
import { Select } from '@/components/ui/Select';
import { TransactionList } from '@/components/transactions/TransactionList';
import {
  useTransactionsByEvent,
  useRecentTransactions,
} from '@/hooks/useTransactions';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import { useEvents } from '@/hooks/useEvents';
import { formatIDR, formatNumber, cn } from '@/lib/format';
import type { PaymentStatus } from '@/types/domain';

type StatusFilter = PaymentStatus | 'all';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Semua' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending_delivery', label: 'Pending' },
  { value: 'partially_returned', label: 'Partial' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function Transactions() {
  const { activeEventId, activeEvent } = useActiveEvent();
  const { data: events } = useEvents();

  // Build event-name lookup for the table. Used to show a small
  // subtitle next to each invoice number when the list spans multiple
  // events — clarifies that INV-0001 in event A and INV-0001 in event
  // B are different transactions.
  const eventNameById = useMemo(() => {
    const m = new Map<string, string>();
    (events ?? []).forEach((e) => m.set(e.id, e.name));
    return m;
  }, [events]);

  // Prefer event-scoped, fall back to recent (last 100). When the user
  // picks a specific event from the dropdown, scope to that event.
  const [eventFilter, setEventFilter] = useState<string>('');
  const effectiveEventId = eventFilter || activeEventId;
  const eventScoped = useTransactionsByEvent(effectiveEventId);
  const recent = useRecentTransactions(100);
  const data = effectiveEventId ? eventScoped.data : recent.data;
  const isLoading = effectiveEventId ? eventScoped.isLoading : recent.isLoading;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filtered = useMemo(() => {
    let list = data ?? [];
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      list = list.filter((t) => {
        if (t.invoiceNumber.toLowerCase().includes(s)) return true;
        if (t.customerSnapshot?.name.toLowerCase().includes(s)) return true;
        if (t.customerSnapshot?.phone.includes(s)) return true;
        return false;
      });
    }
    if (statusFilter !== 'all') {
      list = list.filter((t) => t.paymentStatus === statusFilter);
    }
    return list;
  }, [data, search, statusFilter]);

  const showEventName = !effectiveEventId;

  const totals = useMemo(() => {
    const list = filtered;
    return {
      count: list.length,
      revenue: list
        // Cancelled transactions don't count as revenue.
        .filter((t) => t.paymentStatus !== 'cancelled')
        .reduce((s, t) => s + t.grandTotal, 0),
      items: list.reduce(
        (s, t) => s + t.items.reduce((s2, i) => s2 + i.qty, 0),
        0,
      ),
    };
  }, [filtered]);

  return (
    <>
      <PageHeader
        overline={
          activeEvent && !eventFilter
            ? `Event: ${activeEvent.name}`
            : eventFilter
              ? `Event: ${eventNameById.get(eventFilter) ?? '…'}`
              : 'All events · 100 transaksi terbaru'
        }
        title="Transactions"
        description="Riwayat semua transaksi — paid, pending delivery, return. Click row untuk lihat invoice."
      />

      {!effectiveEventId && (
        <Card className="mb-6 border-warning/40 bg-warning/5">
          <div className="flex items-start gap-2">
            <Filter size={14} className="text-warning mt-0.5 flex-shrink-0" />
            <p className="text-small text-warning">
              Tidak ada event dipilih — menampilkan 100 transaksi terbaru dari
              semua event. Pilih event di filter di bawah untuk lihat per-event.
            </p>
          </div>
        </Card>
      )}

      {/* Event filter — explicit so the user always knows the scope. */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="w-full sm:max-w-xs">
          <Select
            label="Filter event"
            value={eventFilter || (activeEventId ?? '')}
            onChange={(v) => setEventFilter(v)}
          >
            <option value="">Semua event</option>
            {(events ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} ({e.code}) {e.status === 'closed' ? '· closed' : ''}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">
            Transaksi
          </p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">
            {formatNumber(totals.count)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">
            Item terjual
          </p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">
            {formatNumber(totals.items)}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-overline uppercase text-ink-subtle font-medium">
            Total penjualan
          </p>
          <p className="text-metric font-display font-semibold mt-2 text-ink">
            {formatIDR(totals.revenue)}
          </p>
        </Card>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-xs">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Cari invoice / customer / phone…"
          />
        </div>
        <div
          role="tablist"
          className="inline-flex p-1 rounded bg-canvas border border-line overflow-x-auto"
        >
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={statusFilter === opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-small font-medium transition-colors whitespace-nowrap',
                statusFilter === opt.value
                  ? 'bg-surface text-ink shadow-hover'
                  : 'text-ink-subtle hover:text-ink',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <Card padding="none">
        {isLoading ? (
          <div className="py-16 text-center text-ink-subtle text-body">
            <Receipt size={20} className="mx-auto mb-2 opacity-50" />
            Loading transactions…
          </div>
        ) : filtered.length === 0 && (data?.length ?? 0) > 0 ? (
          <EmptyState
            icon={<Filter size={20} strokeWidth={1.5} />}
            title="Tidak ada transaksi yang cocok"
            description="Coba ubah filter status atau kata kunci pencarian."
          />
        ) : (
          <TransactionList
            transactions={filtered}
            showEventName={showEventName}
            eventNameById={eventNameById}
          />
        )}
      </Card>
    </>
  );
}
